import { NgClass, NgTemplateOutlet } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  inject,
  input,
  output,
  signal,
} from '@angular/core';
import { RouterLink } from '@angular/router';

import { I18nService } from '@core/i18n-legacy';
import {
  ButtonComponent,
  HeadingComponent,
  IconButtonComponent,
  IconComponent,
  PlaceloaderComponent,
} from '@core/user-interface-legacy';

import type { ProductUpdate } from '../../models/product-update.model';
import {
  MainTab,
  ProductTab,
  ProductUpdatesStateService,
} from '../../services/product-updates-state.service';
import { ThemeService } from '../../services/theme.service';
import { CategoryBadgeComponent } from '../category-badge/category-badge.component';
import { MobileMenuComponent } from '../mobile-menu/mobile-menu.component';

type StickyState = 'hidden' | 'visible' | 'hiding';
export type HeaderMode = 'feed' | 'article';

@Component({
  selector: 'pu-header',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    NgClass,
    NgTemplateOutlet,
    RouterLink,
    ButtonComponent,
    HeadingComponent,
    IconButtonComponent,
    IconComponent,
    PlaceloaderComponent,
    CategoryBadgeComponent,
    MobileMenuComponent,
  ],
  templateUrl: './header.component.html',
  styleUrls: ['./header.component.less'],
})
export class HeaderComponent {
  private readonly themeService = inject(ThemeService);

  public readonly translationService = inject(I18nService);
  public readonly stateService = inject(ProductUpdatesStateService);

  public readonly mode = input<HeaderMode>('feed');
  public readonly article = input<ProductUpdate | null>(null);
  public readonly activeMainTab = input<MainTab>('whats-new');
  public readonly activeProductTab = input<ProductTab>('all');
  public readonly stickyState = input<StickyState>('hidden');

  public readonly mainTabChange = output<MainTab>();
  public readonly productTabChange = output<ProductTab>();
  public readonly languageChange = output<string>();

  public readonly isDarkMode = this.themeService.isDarkMode;
  public readonly isLanguageDropdownOpen = signal(false);
  public readonly isMobileMenuOpen = signal(false);

  public readonly primaryTranslation = computed(() => {
    const article = this.article();
    if (!article) return null;
    const locale = this.stateService.currentLocale();
    // Try current locale first, then fall back to primary locale, then first translation
    return (
      article.translations.find(t => t.locale === locale) ??
      article.translations.find(t => t.locale === article.primaryLocale) ??
      article.translations[0]
    );
  });

  public readonly productTabs: ProductTab[] = ['all', 'ai', 'hire', 'manage', 'grow'];

  public getProductTabLabel(tab: ProductTab): string {
    const keyMap: Record<ProductTab, string> = {
      all: 'filters.all',
      ai: 'filters.ai',
      hire: 'filters.hire',
      manage: 'filters.manage',
      grow: 'filters.grow',
    };
    return this.translationService.translate(keyMap[tab]);
  }

  public onMainTabChange(tab: MainTab): void {
    this.mainTabChange.emit(tab);
  }

  public onProductTabChange(tab: ProductTab): void {
    this.productTabChange.emit(tab);
  }

  public toggleTheme(): void {
    this.themeService.toggleTheme();
  }

  public toggleLanguageDropdown(): void {
    this.isLanguageDropdownOpen.update(current => !current);
  }

  public selectLanguage(locale: string): void {
    this.isLanguageDropdownOpen.set(false);
    this.languageChange.emit(locale);
  }

  public getLanguageLabel(locale: string): string {
    const keyMap: Record<string, string> = {
      en: 'language.en',
      de: 'language.de',
      fr: 'language.fr',
      nl: 'language.nl',
      fi: 'language.fi',
      sv: 'language.sv',
    };
    return this.translationService.translate(keyMap[locale]) || locale.toUpperCase();
  }

  public getActiveMainTabLabel(): string {
    const tab = this.activeMainTab();
    switch (tab) {
      case 'whats-new':
        return this.translationService.translate('nav.whats_new');
      case 'coming-soon':
        return this.translationService.translate('nav.coming_soon');
      case 'timeline':
        return this.translationService.translate('nav.timeline');
      case 'changelog':
        return this.translationService.translate('nav.changelog');
      default:
        return '';
    }
  }

  public toggleMobileMenu(): void {
    this.isMobileMenuOpen.update(open => !open);
  }

  public closeMobileMenu(): void {
    this.isMobileMenuOpen.set(false);
  }

  public onMobileMainTabChange(tab: MainTab): void {
    this.mainTabChange.emit(tab);
    this.closeMobileMenu();
  }

  public onMobileProductTabChange(tab: ProductTab): void {
    this.productTabChange.emit(tab);
    this.closeMobileMenu();
  }
}
